import { Config } from "@wagmi/core"

import type { IEventBus } from "@/domains/events"
import type { ITokenLookup } from "@/domains/tokens/types"
import type { IVoteOnChainTransport } from "@/domains/vote/interfaces"
import { LcAddress } from "@/domains/core"
import { OrchestratedQueryStore } from "@/domains/library"
import { LockRewardRepository, LockRewardsOnChainTransport } from "@/domains/lock-rewards"
import { WalletConnectionStore } from "@/domains/wallet"

import { LockApiTransport } from "./lock-api-transport"
import { LockOnChainTransport } from "./lock-on-chain-transport"
import { LockOrchestratorFactory } from "./lock-orchestrator-factory"
import { LockOrchestratorRefreshStrategy } from "./lock-orchestrator-refresh-strategy"
import { LockRepository } from "./lock-repository"
import { LockStoreFactory } from "./lock-store-factory"
import { LockNamespace } from "./types"

export type LockNamespaceFactoryParams = {
  config: Config
  lockRewardRepository: LockRewardRepository
  lockRewardsOnChainTransport: LockRewardsOnChainTransport
  orchestratedQueryStore: OrchestratedQueryStore
  tokenLookup: ITokenLookup
  wallet: WalletConnectionStore
  eventBus: IEventBus
  lockOnChainTransport: LockOnChainTransport
  voteOnChainTransport: IVoteOnChainTransport
}

export const LockNamespaceFactory = {
  create({
    config,
    eventBus,
    lockRewardRepository,
    lockRewardsOnChainTransport,
    orchestratedQueryStore,
    tokenLookup,
    wallet,
    lockOnChainTransport,
    voteOnChainTransport,
  }: LockNamespaceFactoryParams): LockNamespace {
    const lockApiTransport = new LockApiTransport(lockOnChainTransport)
    const lockRepository = new LockRepository(eventBus)

    const lockOrchestrator = LockOrchestratorFactory.create(
      lockApiTransport,
      lockRepository,
      wallet,
      orchestratedQueryStore,
      lockRewardsOnChainTransport,
      lockRewardRepository,
      tokenLookup,
      lockOnChainTransport,
      eventBus,
      voteOnChainTransport,
    )

    // Create refresh strategy for lock stores
    // The orchestrator's query is guaranteed to be initialized by LockOrchestratorFactory
    const lockRefreshStrategy = new LockOrchestratorRefreshStrategy(lockOrchestrator.query)

    const lockStoreFactory = new LockStoreFactory(
      lockRewardsOnChainTransport,
      lockRewardRepository,
      tokenLookup,
      orchestratedQueryStore,
      lockRefreshStrategy,
    )

    return {
      Repository: lockRepository,
      Orchestrator: lockOrchestrator,
      Factory: lockStoreFactory,
    }
  },
}
